import React, { useRef } from "react";
import { Mic, Video, Images, X } from "lucide-react";

const MAX_PROCESS = 8;
const btn = "flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5";

const Picker = ({ accept, multiple, onFiles, icon: Icon, label, testid }) => {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" data-testid={`${testid}-input`}
        onChange={(e) => { const f = Array.from(e.target.files || []); if (f.length) onFiles(f); e.target.value = ""; }} />
      <button type="button" onClick={() => ref.current?.click()} data-testid={testid} className={btn}><Icon size={13} /> {label}</button>
    </>
  );
};

const Chip = ({ label, onRemove, testid }) => (
  <span className="inline-flex items-center gap-1.5 bg-white/5 border border-white/10 px-2 py-1 font-body text-[11px] text-zinc-300" data-testid={testid}>
    {label}<button type="button" onClick={onRemove} aria-label="Remove" className="text-zinc-500 hover:text-red-400"><X size={11} /></button>
  </span>
);

// Builder: optional story extras for an artwork. Files upload when the piece is saved.
export const ArtworkStoryFields = ({ art, onArt, extras, onExtras, files, onFiles }) => {
  const procCount = (extras.process || []).length + files.process.length;
  return (
    <div className="border border-white/8 bg-[#0A0A0A]/60 p-2.5 space-y-2" data-testid="artwork-story-fields">
      <div className="font-body text-[11px] uppercase tracking-wider text-zinc-400">Add the story (all optional)</div>
      <textarea value={art.story || ""} onChange={(e) => onArt({ ...art, story: e.target.value })} rows={3} maxLength={5000} data-testid="artwork-story"
        placeholder="The story behind this piece: what inspired it, how it was made, what it means to you"
        className="w-full bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
      <div className="flex flex-wrap gap-2 items-center">
        {extras.voice || files.voice
          ? <Chip label={files.voice ? `Voice note: ${files.voice.name}` : "Voice note added"} testid="artwork-voice-chip" onRemove={() => { onFiles({ ...files, voice: null }); onExtras({ ...extras, voice: null }); }} />
          : <Picker accept="audio/*" icon={Mic} label="Voice note" testid="artwork-voice-upload" onFiles={([f]) => onFiles({ ...files, voice: f })} />}
        {extras.video || files.video
          ? <Chip label={files.video ? `Studio video: ${files.video.name}` : "Studio video added"} testid="artwork-video-chip" onRemove={() => { onFiles({ ...files, video: null }); onExtras({ ...extras, video: null }); }} />
          : <Picker accept="video/*" icon={Video} label="Studio video" testid="artwork-video-upload" onFiles={([f]) => onFiles({ ...files, video: f })} />}
        {procCount < MAX_PROCESS && <Picker accept="image/*" multiple icon={Images} label={`Process images (${procCount}/${MAX_PROCESS})`} testid="artwork-process-upload"
          onFiles={(fs) => onFiles({ ...files, process: [...files.process, ...fs].slice(0, MAX_PROCESS - (extras.process || []).length) })} />}
      </div>
      {procCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {(extras.process || []).map((m, i) => <Chip key={m.path} label={`Process ${i + 1}`} testid={`artwork-process-chip-${i}`} onRemove={() => onExtras({ ...extras, process: extras.process.filter((_, j) => j !== i) })} />)}
          {files.process.map((f, i) => <Chip key={`n${i}`} label={`New ${i + 1}: ${f.name}`} testid={`artwork-process-new-${i}`} onRemove={() => onFiles({ ...files, process: files.process.filter((_, j) => j !== i) })} />)}
        </div>
      )}
      <label className="flex items-center gap-1.5 font-body text-xs text-zinc-400 pt-1"><input type="checkbox" checked={art.inquire !== false} onChange={(e) => onArt({ ...art, inquire: e.target.checked })} data-testid="artwork-inquire" className="accent-[#14A89A]" /> Show "Interested in this piece?" so members can message you</label>
    </div>
  );
};
